import * as THREE from 'three';
import { glowRingTexture } from '@/world/scenery/propKit';
import { itemFxCalm } from '@/entities/fx/itemFx';

/**
 * BoostFx.ts — everything a BOOSTING car wears (M145, owner: "for the speed
 * boost, add more of a zooming boost effect"). Replaces the single blue cone
 * the car had since M12.
 *
 *  - TWIN AFTERBURNERS: a hot white core inside a blue plume at each exhaust,
 *    flickering and stretching with the boost.
 *  - LIGHT TRAILS: two streaks pulled back off the tail corners that grow as
 *    the boost builds — the long-exposure tail-light look.
 *  - WIND STREAKS: thin lines of air tearing past the body, so the car itself
 *    looks like it is cutting through something (rivals show it too).
 *  - IGNITION RING: one shock ring that pops out behind the car the instant
 *    the boost lights.
 *
 * Lives in the car's leaning body (car-local: -Z is the tail). Additive on
 * purpose — these are light, and the bloom pass does the rest. Presentation
 * only.
 */

const PIPE_X = 0.4;
const TRAIL_MAX = 8; // world units at full boost
const STREAKS = 7;
const RING_TIME = 0.42;

let trailTex: THREE.CanvasTexture | null = null;
/** Bright at the car (v = 1), gone at the far end (v = 0), soft across. */
function trailTexture(): THREE.CanvasTexture {
  if (trailTex) return trailTex;
  const c = document.createElement('canvas');
  c.width = 16;
  c.height = 64;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  const img = x.createImageData(16, 64);
  for (let py = 0; py < 64; py++) {
    const along = 1 - py / 63; // canvas top = v 1 = the car end
    for (let px = 0; px < 16; px++) {
      const across = 1 - Math.abs((px + 0.5) / 8 - 1);
      const a = Math.pow(along, 1.6) * Math.pow(across, 1.4);
      const i = (py * 16 + px) * 4;
      img.data[i] = 255;
      img.data[i + 1] = 255;
      img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(255 * a);
    }
  }
  x.putImageData(img, 0, 0);
  trailTex = new THREE.CanvasTexture(c);
  trailTex.userData.shared = true;
  return trailTex;
}

/** A unit ribbon running from z = 0 back to z = -1 (v = 1 at the car end). */
function ribbonGeometry(vertical: boolean): THREE.BufferGeometry {
  // PlaneGeometry starts in XY with v running up +Y. Either way the plane's
  // v axis ends up along +Z, so after the shift v = 1 sits at the car.
  const g = new THREE.PlaneGeometry(1, 1);
  if (vertical) {
    g.rotateZ(Math.PI / 2); // width → Y, length → -X
    g.rotateY(Math.PI / 2); // length → +Z
  } else {
    g.rotateX(Math.PI / 2); // lying flat: width along X, length → +Z
  }
  g.translate(0, 0, -0.5);
  return g;
}

export class BoostFx {
  readonly group = new THREE.Group();
  private readonly plumeMat: THREE.MeshBasicMaterial;
  private readonly coreMat: THREE.MeshBasicMaterial;
  private readonly plumes: THREE.Mesh[] = [];
  private readonly cores: THREE.Mesh[] = [];
  private readonly trailMat: THREE.MeshBasicMaterial;
  private readonly trails: THREE.Group[] = [];
  private readonly streakMat: THREE.MeshBasicMaterial;
  private readonly streaks: { node: THREE.Group; x: number; y: number; len: number; off: number }[] =
    [];
  private readonly ring: THREE.Mesh;
  private readonly ringMat: THREE.MeshBasicMaterial;
  private ringT = 0;
  private t = 0;

  constructor() {
    const additive = {
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    } as const;

    // ---- afterburners ----
    this.plumeMat = new THREE.MeshBasicMaterial({ color: 0x4fb8ff, ...additive });
    this.coreMat = new THREE.MeshBasicMaterial({ color: 0xeaffff, ...additive });
    const plumeGeo = new THREE.ConeGeometry(0.3, 1.9, 10, 1, true);
    plumeGeo.rotateX(-Math.PI / 2); // tip points back (-Z)
    plumeGeo.translate(0, 0, -0.95); // base at the pipe
    const coreGeo = new THREE.ConeGeometry(0.15, 1.15, 8, 1, true);
    coreGeo.rotateX(-Math.PI / 2);
    coreGeo.translate(0, 0, -0.575);
    for (const sx of [-PIPE_X, PIPE_X]) {
      const plume = new THREE.Mesh(plumeGeo, this.plumeMat);
      plume.position.set(sx, 0.36, -2.02);
      const core = new THREE.Mesh(coreGeo, this.coreMat);
      core.position.set(sx, 0.36, -2.02);
      this.plumes.push(plume);
      this.cores.push(core);
      this.group.add(plume, core);
    }

    // ---- light trails off the tail corners (a flat + an upright ribbon each) ----
    this.trailMat = new THREE.MeshBasicMaterial({
      color: 0x8fe6ff,
      map: trailTexture(),
      side: THREE.DoubleSide,
      ...additive,
    });
    const flat = ribbonGeometry(false);
    const upright = ribbonGeometry(true);
    for (const sx of [-0.82, 0.82]) {
      const node = new THREE.Group();
      node.position.set(sx, 0.8, -1.98);
      node.add(new THREE.Mesh(flat, this.trailMat), new THREE.Mesh(upright, this.trailMat));
      this.trails.push(node);
      this.group.add(node);
    }

    // ---- wind streaks past the body ----
    this.streakMat = new THREE.MeshBasicMaterial({
      color: 0xdff6ff,
      map: trailTexture(),
      side: THREE.DoubleSide,
      ...additive,
    });
    for (let i = 0; i < STREAKS; i++) {
      const node = new THREE.Group();
      node.add(new THREE.Mesh(flat, this.streakMat), new THREE.Mesh(upright, this.streakMat));
      const side = i % 2 === 0 ? 1 : -1;
      this.streaks.push({
        node,
        x: side * (1.35 + ((i * 0.37) % 1) * 1.1),
        y: 0.35 + ((i * 0.61) % 1) * 1.5,
        len: 2.4 + ((i * 0.83) % 1) * 2.2,
        off: (i * 0.29) % 1,
      });
      this.group.add(node);
    }

    // ---- the ignition ring ----
    this.ringMat = new THREE.MeshBasicMaterial({
      color: 0xbdf0ff,
      map: glowRingTexture(),
      side: THREE.DoubleSide,
      ...additive,
    });
    this.ring = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.ringMat);
    this.ring.position.set(0, 0.7, -2.3);
    this.ring.visible = false;
    this.group.add(this.ring);

    this.group.visible = false;
  }

  /** The boost just lit: pop the shock ring. */
  ignite(): void {
    if (itemFxCalm()) return;
    this.ringT = RING_TIME;
  }

  /** @param flare 0..1 — the smoothed boost amount. */
  update(flare: number, dt: number): void {
    this.t += dt;
    const live = flare > 0.02 || this.ringT > 0;
    this.group.visible = live;
    if (!live) return;
    const calm = itemFxCalm();
    const t = this.t;

    // Afterburners: two flicker rates beating against each other.
    const flick = 1 + Math.sin(t * 47) * 0.16 + Math.sin(t * 29 + 1.7) * 0.1;
    this.plumeMat.opacity = flare * 0.7;
    this.coreMat.opacity = flare * 0.95;
    for (let i = 0; i < this.plumes.length; i++) {
      const wob = 1 + Math.sin(t * 38 + i * 2.1) * 0.08;
      this.plumes[i].scale.set(wob, wob, (0.35 + flare * 1.15) * flick);
      this.cores[i].scale.set(1, 1, (0.4 + flare * 0.9) * (2 - flick));
    }

    // Light trails: grow with the boost, shimmer a touch.
    const trailLen = TRAIL_MAX * flare * (0.92 + Math.sin(t * 21) * 0.08);
    this.trailMat.opacity = calm ? 0 : flare * 0.85;
    for (const tr of this.trails) tr.scale.set(0.34, 0.2, Math.max(0.01, trailLen));

    // Wind streaks: each loops nose → well behind the tail.
    this.streakMat.opacity = calm ? 0 : flare * 0.5;
    if (!calm) {
      for (const s of this.streaks) {
        const k = (t * 2.6 + s.off) % 1; // 0 at the nose, 1 far behind
        s.node.position.set(s.x, s.y, 3.2 - k * 11);
        s.node.scale.set(0.07, 0.07, s.len * (0.5 + flare * 0.5));
      }
    }

    // Ignition ring: fast out, fading as it goes.
    if (this.ringT > 0) {
      this.ringT = Math.max(0, this.ringT - dt);
      const k = 1 - this.ringT / RING_TIME; // 0 → 1
      const size = 1.2 + k * 7;
      this.ring.visible = true;
      this.ring.scale.set(size, size, 1);
      this.ring.position.z = -2.3 - k * 2.2;
      this.ringMat.opacity = (1 - k) * (1 - k) * 0.95;
    } else if (this.ring.visible) {
      this.ring.visible = false;
    }
  }
}
